/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import ButtonGroup from '../../../../client/js/components/form/ButtonGroup'
import FormField from '../../../../client/js/components/form/fields/FormField'
import FormSaveButton from '../../../../client/js/components/form/buttons/FormSaveButton'
import SmartForm from '../../../../client/js/components/form/SmartForm'
import RecordMetas from '../../../../client/js/components/RecordMetas'
import {
  createFieldInitializer,
  createFieldValidator,
  createFormValidator,
  removeUnknownFields
} from '../../../../client/js/lib/form'
import { AuthContext } from '../../../auth/components/AuthProvider'
import {
  INVOICE_CREDIT,
  INVOICE_ESTIMATION,
  INVOICE_LESSOR_FIRST_INVOICE,
  INVOICE_MANAGEMENT,
  INVOICE_OWNER,
  INVOICE_PROFORMA,
  INVOICE_SALE,
  INVOICE_TENANT,
  INVOICE_TENANT_FIRST_INVOICE
} from '../../enums/InvoiceTypes.mjs'
import { TRANSACTION_SALE } from '../../enums/TransactionCodes.mjs'
import { inverseAmounts } from '../../lib/common/amount.mjs'
import { generateInvoiceReference } from '../../lib/common/invoice-reference.mjs'
import { updateExpenseFromAmount } from '../../lib/common/transaction.mjs'
import { randomId } from '../../lib/random.mjs'
import { fetchEstate } from '../../lib/rest/estateAPIClient'
import { patchInvoice, postInvoice } from '../../lib/rest/invoiceAPIClient'
import { fetchLease } from '../../lib/rest/leaseAPIClient'
import InvoiceSchema from '../../schemas/InvoiceSchema.mjs'
import CustomerField from '../fields/CustomerField'
import EstateField from '../fields/EstateField'
import LeaseField from '../fields/LeaseField'
import FormSection from '../FormSection'
import InvoiceDetails from './InvoiceDetails'
import useInvoiceTypeOptions from './useInvoiceTypeOptions'
import { useQuery } from '@tanstack/react-query'
import { queryClient } from '../../../../client/js/lib/query'
import { getInvoiceListLink } from '../../lib/links/invoiceLinks'
import FormResetButton from '../../../../client/js/components/form/buttons/FormResetButton'
import { Button, Select, SimpleGrid, Stack, Switch } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import InvoiceFileField from '../fields/InvoiceFileField'
import LockedInput from '../inputs/LockedInput'
import FormDateField from '../../../../client/js/components/form/fields/FormDateField'
import useCustomForm from '../../../../client/js/hooks/useCustomForm'

export type InvoiceFormProps = {
  initialValues: Partial<Invoice>,
  redirectUrl?: string
}

const filteredInvoiceTypes: InvoiceType[] = [
  INVOICE_OWNER,
  INVOICE_TENANT,
  INVOICE_CREDIT,
  INVOICE_ESTIMATION,
  INVOICE_PROFORMA,
  INVOICE_SALE
]

function InvoiceForm (props: InvoiceFormProps) {
  const {
    initialValues,
    redirectUrl = getInvoiceListLink()
  } = props

  const intl = useIntl()
  const { hasPermission } = useContext(AuthContext)
  const editable = hasPermission('write:invoice')
  const [locked, setLocked] = useState<boolean>(initialValues?.paid)
  const id = initialValues?.id
  const userCanUnlock = hasPermission('unlock:invoice') && id != null

  const form = useCustomForm({
    disabled: !editable,
    initialValues: useMemo<Partial<Invoice>>(() => ({
      total: 0,
      vat: 0,
      cps: 0,
      paid: false,
      tmpRef: randomId(),
      ...inverseAmounts(initialValues),
      // Inverse transaction amounts.
      Transactions: initialValues?.Transactions?.map(inverseAmounts)
    }), [initialValues]),
    initializeField: createFieldInitializer(InvoiceSchema),
    reinitialize: true,
    validate: createFormValidator(InvoiceSchema),
    validateField: createFieldValidator(InvoiceSchema),
    onSubmit: (submitValues) => {
      const finalValues = removeUnknownFields({
        ...inverseAmounts(submitValues),
        // Inverse transaction amounts.
        Transactions: submitValues.Transactions
          ?.map(inverseAmounts)
          // Set expense based on amounts.
          ?.map(updateExpenseFromAmount)
      }, InvoiceSchema)
      return id ? patchInvoice(id, finalValues) : postInvoice(finalValues)
    },
    onSuccess () {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
    transform (mutation, values) {
      const mut = { ...mutation }

      // Update totals.
      if (mut.Transactions != null || Object.keys(mut).find((el) => el.startsWith('Transactions')) != null) {
        const transactions = values.Transactions ?? []

        mut.cps = 0
        mut.vat = 0
        mut.total = 0

        transactions.forEach((el) => {
          mut.cps = (mut.cps as number) + el.cps
          mut.vat = (mut.vat as number) + el.vat
          mut.total = (mut.total as number) + el.amount + el.vat + el.cps
        })
      }
      return mut
    }
  })
  const { setValues, values } = form

  const invoiceTypeOptions = useInvoiceTypeOptions(true, id == null ? filteredInvoiceTypes : undefined)

  // Estate.
  const estateQuery = useQuery({
    enabled: values.estateId != null,
    queryKey: ['estate', values.estateId],
    queryFn: () => fetchEstate(values.estateId)
  })
  const { data: estate } = estateQuery

  // Lease.
  const leaseQuery = useQuery({
    enabled: values.leaseId != null,
    queryKey: ['lease', values.leaseId],
    queryFn: () => fetchLease(values.leaseId)
  })
  const { data: lease } = leaseQuery

  const customersFilter = useMemo(() => ({
    leaseId: values?.leaseId
  }), [values?.leaseId])

  const estatesFilter = useMemo(() => ({
    // Filter estates on sale.
    transaction: values.type === INVOICE_SALE
      ? TRANSACTION_SALE
      : undefined,
    // Filter by owner only.
    ownerId: [
      INVOICE_OWNER,
      INVOICE_LESSOR_FIRST_INVOICE,
      INVOICE_MANAGEMENT
    ].includes(values.type)
      ? values.customerId
      : undefined,
    // Filter by tenant only.
    tenantId: [
      INVOICE_TENANT_FIRST_INVOICE,
      INVOICE_TENANT
    ].includes(values.type)
      ? values.customerId
      : undefined
  }), [values.customerId, values.type])

  const leasesFilter = useMemo(() => ({
    customerId: values.customerId,
    estateId: values.estateId
  }), [values.customerId, values.estateId])

  const isDisabled = useMemo(() => (
    initialValues.id != null || values.paid
  ), [initialValues.id, values.paid])

  // fixme handle update on estate change
  useEffect(() => {
    // Generate invoice reference.
    if (values.id == null && values.type != null && values.dueAt != null && estate != null) {
      const reference = generateInvoiceReference(values, estate.reference)
      if (reference !== values.reference) {
        setValues({ reference }, { partial: true })
      }
    }
  }, [estate, setValues, values])

  // fixme handle update on lease change
  // Set customer from lease and invoice type.
  useEffect(() => {
    if (lease != null && initialValues.customerId == null) {
      if (values.type === INVOICE_TENANT) {
        setValues({ customerId: lease.tenantId }, { partial: true })
      } else if (values.type === INVOICE_OWNER) {
        setValues({ customerId: lease.lessorId }, { partial: true })
      } else if (values.type == null && values.customerId != null) {
        setValues({ customerId: null }, { partial: true })
      }
    }
  }, [initialValues.customerId, lease, setValues, values.customerId, values.type])

  // fixme handle update on lease change
  // Set estate from lease.
  useEffect(() => {
    if (lease != null && initialValues.estateId == null) {
      setValues({ estateId: lease.estateId }, { partial: true })
    }
  }, [initialValues.estateId, lease, setValues])

  return (
    <SmartForm
      context={form}
      redirectUrl={id == null ? redirectUrl : undefined}
    >
      <Stack>
        {initialValues
          ? <RecordMetas metas={initialValues} />
          : null}

        <SimpleGrid cols={{ lg: 2 }}>
          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'general',
              defaultMessage: 'Général'
            })}
          >
            <SimpleGrid cols={{ lg: 2 }}>
              <FormField
                disabled={isDisabled}
                label={intl.formatMessage({
                  id: 'type',
                  defaultMessage: 'Type'
                })}
                name="type"
                render={Select}
                data={invoiceTypeOptions}
              />
            </SimpleGrid>

            <CustomerField
              disabled={
                isDisabled ||
                initialValues?.customerId != null ||
                values.type == null ||
                values.estateId != null
              }
              queryFilter={customersFilter}
            />
            <EstateField
              disabled={
                isDisabled ||
                initialValues?.estateId != null ||
                values.type == null ||
                values.customerId == null ||
                values.leaseId != null
              }
              queryFilter={estatesFilter}
            />
            <LeaseField
              disabled={
                isDisabled ||
                initialValues?.leaseId != null ||
                values.customerId == null ||
                values.estateId == null
                // || values.type !== INVOICE_TENANT // todo disable or not ?
              }
              queryFilter={leasesFilter}
            />
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'date_and_reference',
              defaultMessage: 'Date et référence'
            })}
          >
            <SimpleGrid cols={{ lg: 2 }}>
              <FormField
                disabled={isDisabled || values.type !== INVOICE_ESTIMATION}
                label={intl.formatMessage({
                  id: 'reference',
                  defaultMessage: 'Référence'
                })}
                name="reference"
              />
            </SimpleGrid>

            <SimpleGrid cols={{ lg: 2 }}>
              <FormDateField
                disabled={isDisabled}
                label={intl.formatMessage({
                  id: 'due_date',
                  defaultMessage: 'Échéance'
                })}
                name="dueAt"
              />
            </SimpleGrid>

            <SimpleGrid cols={{ lg: 2 }}>
              <FormDateField
                disabled={!values.paid || !hasPermission('change_paid:invoice')}
                label={intl.formatMessage({
                  id: 'paid_at',
                  defaultMessage: 'Payée le'
                })}
                name="paidAt"
              />
              <FormField
                disabled={locked || !hasPermission('change_paid:invoice')}
                label={intl.formatMessage({
                  id: 'paid',
                  defaultMessage: 'Payé'
                })}
                name="paid"
                render={Switch}
                type="checkbox"
              />
            </SimpleGrid>

            <InvoiceFileField
              label={intl.formatMessage({
                id: 'scanned_file',
                defaultMessage: 'Facture scannée'
              })}
              invoiceId={id}
              tmpRef={values.tmpRef}
            />
          </FormSection>
        </SimpleGrid>

        <FormSection
          defaultOpened
          title={intl.formatMessage({
            id: 'invoice_details',
            defaultMessage: 'Détails de la facture'
          })}
        >
          <InvoiceDetails locked={locked} />
        </FormSection>

        {editable
          ? <ButtonGroup>
            {userCanUnlock && initialValues?.paid
              ? <LockedInput
                onChange={setLocked}
                value={locked}
              />
              : null}

            {values?.id != null
              ? <Button
                to={`/api/invoices/${values?.id}/file`}
                rel="noopener"
                component={AnchorLink}
                target="_blank"
                leftSection={
                  <FontAwesomeIcon
                    fixedWidth
                    icon={faDownload}
                  />}
              >
                {intl.formatMessage({
                  id: 'download',
                  defaultMessage: 'Télécharger'
                })}
              </Button>
              : null}

            <FormResetButton />
            <FormSaveButton />
          </ButtonGroup>
          : null}
      </Stack>
    </SmartForm>
  )
}

export default InvoiceForm
